import { HTMLAttributes, InputHTMLAttributes, KeyboardEvent, MouseEvent, ReactElement, ReactNode } from 'react'
import Box from '@mui/material/Box'
import InputAdornment from '@mui/material/InputAdornment'
import type { SxProps, Theme } from '@mui/material/styles'
import { EUI_FORM_MAX_WIDTH } from '../euiParity'
import { FormRowInjectedProps } from './types'

export const DEFAULT_PLACEHOLDER = 'Search for or select'

/**
 * The EUI-shaped presentation props the shared combo boxes accept. Listed
 * explicitly rather than picked off `EuiComboBoxProps` so the parity surface the
 * MUI branch supports is visible in one place.
 */
export type EuiPresentationProps = {
	isLoading?: boolean,
	isDisabled?: boolean,
	isInvalid?: boolean,
	isClearable?: boolean,
	fullWidth?: boolean,
	placeholder?: string,
	noSuggestions?: boolean,
	id?: string,
	'aria-label'?: string,
	'data-test-subj'?: string,
	'data-testid'?: string,
	/** EUI's leading addon. Mapped to a MUI start `InputAdornment` (sc-567879). */
	prepend?: string | ReactElement | ( string | ReactElement )[],
} & FormRowInjectedProps;

export type MuiComboBoxPresentation = {
	autocomplete: {
		loading: boolean,
		disabled: boolean,
		fullWidth: boolean,
		clearIcon: ReactNode,
		sx: SxProps<Theme>,
		id?: string,
		'data-test-subj'?: string,
		'data-testid'?: string,
	},
	textField: {
		placeholder: string,
		error: boolean,
		fullWidth: boolean,
		label?: ReactNode,
		helperText?: ReactNode,
		inputProps: InputHTMLAttributes<HTMLInputElement>,
		/** Merged into `params.InputProps`, never spread over it — see `toTextFieldProps`. */
		InputProps?: { startAdornment?: ReactNode },
	},
	/** EUI's `noSuggestions` suppresses the option list entirely. */
	suppressOptions: boolean,
};

/**
 * Autocomplete's root handles every event inside it: click pulls focus to the
 * input and keydown drives its own list. Real callers pass interactive addons
 * (an `EuiPopover` + `EuiSelectable`), so fence those off. Mousedown is left
 * alone: EUI's outside-click detection listens for it on the document.
 */
function stopAutocompleteClick( event: MouseEvent<HTMLElement> ) {
	// Portaled popover content isn't DOM-contained; Autocomplete ignores it already.
	if ( event.currentTarget.contains( event.target as Node ) ) {
		event.stopPropagation()
	}
}

// MUI's own opt-out flag rather than `stopPropagation`, which would also hide
// the keydown from EUI's document-level Escape handler.
function stopAutocompleteKeyDown( event: KeyboardEvent<HTMLElement> & { defaultMuiPrevented?: boolean } ) {
	event.defaultMuiPrevented = true
}

/**
 * EUI's `prepend` is a leading addon accepting a string, an element, or a list
 * of either. MUI's equivalent is a start `InputAdornment`; we adopt MUI's own
 * presentation rather than reproducing EUI's addon chrome.
 *
 * Known gap: EUI renders a string `prepend` as a `<label htmlFor>` for the input;
 * here it is plain adornment text, so it no longer names the input for screen
 * readers. Callers needing an accessible name should pass `aria-label`.
 */
function toStartAdornment( prepend: EuiPresentationProps['prepend'] ): ReactNode {
	if ( prepend === undefined || prepend === null ) {
		return undefined
	}

	if ( Array.isArray( prepend ) && prepend.length === 0 ) {
		return undefined
	}

	return (
		<InputAdornment
			position="start"
			onClick={stopAutocompleteClick}
			onKeyDown={stopAutocompleteKeyDown}
		>
			{/* A lone string goes straight through so InputAdornment applies its text styling. */}
			{Array.isArray( prepend )
				? prepend.map( ( node, i ) => (
					// eslint-disable-next-line react/no-array-index-key -- addons have no stable id
					<Box component="span" key={i}>{node}</Box>
				) )
				: prepend}
		</InputAdornment>
	)
}

/**
 * Builds the final `TextField` props inside `Autocomplete`'s `renderInput`.
 *
 * `params` carries MUI's own wiring — `InputProps.endAdornment` holds the popup
 * indicator and clear button, and `inputProps` the input's a11y attributes — so
 * both objects must be *merged*, never spread over. Centralized here because
 * every combo-box branch needs the identical merge, and getting it wrong
 * silently drops the dropdown arrow.
 */
export function toTextFieldProps<P extends {
	inputProps?: InputHTMLAttributes<HTMLInputElement>,
	InputProps?: Record<string, unknown>,
}>( params: P, textField: MuiComboBoxPresentation['textField'] ) {
	return {
		...params,
		...textField,
		inputProps: { ...params.inputProps, ...textField.inputProps },
		InputProps: { ...params.InputProps, ...textField.InputProps },
	}
}

/**
 * Single EUI → MUI prop map for every dual-render combo box. Keeping it here
 * means `AdvancedComboBox`, `GroupedComboBox`, and the base `ComboBox` can't
 * translate the same prop two different ways.
 */
export function toMuiComboBoxProps( props: EuiPresentationProps ): MuiComboBoxPresentation {
	const fullWidth = props.fullWidth ?? false
	const startAdornment = toStartAdornment( props.prepend )

	return {
		autocomplete: {
			// Known parity gap, not a mapping bug: Autocomplete renders its
			// `loadingText` only when the option list is empty, so a debounced
			// re-search over existing results shows no affordance at all, where
			// EUI spins in the control regardless. Worth revisiting when a view
			// flips to MUI in Phase 3 and this first becomes user-visible.
			'loading': props.isLoading ?? false,
			// `disabled` is FormRow's `isDisabled`, already resolved by the row.
			'disabled': props.isDisabled ?? props.disabled ?? false,
			// EUI form controls are width:100% capped at 400px and `fullWidth` lifts
			// the cap; MUI's `fullWidth` is on/off and an input without it collapses
			// to its content width. So always fill, and express the cap separately.
			'fullWidth': true,
			'sx': { maxWidth: fullWidth ? '100%' : EUI_FORM_MAX_WIDTH },
			// EUI combo boxes are clearable unless explicitly opted out. Hiding the
			// icon (rather than MUI's `disableClearable`) keeps the value nullable.
			'clearIcon': props.isClearable === false ? null : undefined,
			'id': props.id,
			'data-test-subj': props[ 'data-test-subj' ],
			'data-testid': props[ 'data-testid' ],
		},
		textField: {
			placeholder: props.placeholder ?? DEFAULT_PLACEHOLDER,
			// A real OR, not `??`: a call site's own `isInvalid` and FormRow's
			// injected `error` are independent, and either alone means invalid.
			// `??` would let an explicit `isInvalid={false}` mask a row-level
			// error. Mirrors the same call in `currencyField.mui.tsx`.
			error: ( props.isInvalid ?? false ) || ( props.error ?? false ),
			fullWidth: true,
			// Undefined rather than absent when there's no row: TextField only
			// renders a label/helper slot once it has something to put there.
			label: props.label,
			helperText: props.helperText,
			inputProps: props[ 'aria-label' ] ? { 'aria-label': props[ 'aria-label' ] } : {},
			// Omit the key entirely when there is no prepend: an explicit
			// `startAdornment: undefined` would merge over `params.InputProps`,
			// which is where MUI puts selection chips in multiple-select mode.
			...( startAdornment ? { InputProps: { startAdornment } } : {} ),
		},
		suppressOptions: props.noSuggestions ?? false,
	}
}

/**
 * MUI passes `key` inside the option props object; spreading it onto an element
 * warns in React 18, so split it out at every option render site.
 */
export function splitOptionKey( optionProps: HTMLAttributes<HTMLLIElement> & { key?: unknown } ) {
	const { key, ...liProps } = optionProps

	return { key: key as string | undefined, liProps }
}
